import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import {
  DraftSignaturePlacement,
  formatSignatureBoxLabel,
  LETTER_PAGE_HEIGHT_PX,
  LETTER_PAGE_WIDTH_PX,
  measureRenderedSignatureHtml,
  normalizeSignatureHtmlForRender,
  parseSignatureModelFromHtml,
  sanitizeDraftSignaturePlacement,
  SignatureHtmlModel,
} from './signatureLayoutShared';
import { formatSeplReferenceNumber } from '../../utils/helpers';
import './LetterDocument.css';

const IMAGE_CACHE_BUSTER = '20260515-1';
const SIGNATURE_VISUAL_BOOST = 1;
const MM_TO_PX = 96 / 25.4;
const PAGE_HEIGHT_PX = 1122; // A4 at 96 DPI
const LETTERHEAD_SAFE_PX = 140;
const FOOTER_SAFE_PX = 50;
const CONTENT_VCLEARANCE_MM = 3;
const FIRST_PAGE_META_BAND_MM = 14;

// Keep static LetterDocument page chunking aligned with the editor/preview
// geometry so page breaks do not shift after submit/approval.
const BASE_BODY_HEIGHT_PX = Math.floor(
  PAGE_HEIGHT_PX - LETTERHEAD_SAFE_PX - FOOTER_SAFE_PX - CONTENT_VCLEARANCE_MM * 2 * MM_TO_PX,
);
const MAX_BODY_HEIGHT_FIRST_PAGE_PX = Math.floor(
  BASE_BODY_HEIGHT_PX - FIRST_PAGE_META_BAND_MM * MM_TO_PX,
);
const MAX_BODY_HEIGHT_SUBSEQUENT_PAGES_PX = BASE_BODY_HEIGHT_PX;
const MAX_PDF_BODY_HEIGHT_FIRST_PAGE_PX = MAX_BODY_HEIGHT_FIRST_PAGE_PX;
const MAX_PDF_BODY_HEIGHT_PX = MAX_BODY_HEIGHT_SUBSEQUENT_PAGES_PX;

const QR_BOX_SIZE_PX = 64;
const QR_BOX_PADDING_PX = 0;
const QR_INSET_PX = 28;
const FOOTER_VISUAL_SHIFT_PX = 0;
const SIGNATURE_MARKER_CLASS = 'sepl-draft-signature-meta';
const DEFAULT_SIGNATURE_PLACEMENT = { pageIndex: 0, x: 56, y: 850, width: 168, height: 140 };
const LETTER_BODY_FONT_FAMILY = "'Carlito', 'Calibri', 'Segoe UI', Arial, sans-serif";
const LETTER_BODY_LINE_HEIGHT = 1.15;
// Content band geometry — MUST stay in sync with the editor/preview/PDF export
// (CONTENT_LEFT_MM=15, CONTENT_RIGHT_MM=15 in EditorSurface.tsx, PreviewPane.tsx
// and new-editor/io.ts). The A4 page is 210mm wide, leaving 180mm of content,
// so the approved/downloaded letter aligns horizontally with the editor draft
// instead of sitting ~20px too far left and ~20px too wide.
const CONTENT_PAD_LEFT = '15mm';
const CONTENT_PAD_RIGHT = '15mm';
const CONTENT_BAND_WIDTH = '180mm';
// Body content rendering classes for the static (non-editor) letter render.
// Table styling is intentionally NOT included here — it is provided by the
// scoped `.sepl-letter-prose` rules in LetterDocument.css, which mirror the
// editor's `.ProseMirror table` base + `data-table-style` presets. Paragraph,
// list and image spacing is kept here so body pagination stays unchanged.
// Paragraph / list / heading block spacing is governed by the scoped
// `.sepl-letter-prose` rules in LetterDocument.css (mirrored from editor.css)
// so the static render matches the editor and live preview. Only non-spacing
// helpers remain here (whitespace handling, empty-block min-height, image sizing).
const BODY_RENDER_CLASSES =
  '[&_p]:whitespace-pre-wrap [&_p:empty]:min-h-[1em] [&_div]:whitespace-pre-wrap [&_div:empty]:min-h-[1em] [&_li]:whitespace-pre-wrap [&_img]:w-full [&_img]:h-auto [&_img]:block [&_img]:my-0 [&_.sepl-pdf-page-image]:w-full [&_.sepl-pdf-page-image]:max-w-full [&_.sepl-pdf-page-image]:h-auto [&_.sepl-pdf-page-image]:block [&_.sepl-pdf-page-image]:mx-0 [&_.sepl-pdf-page-image]:my-0';

interface SignaturePlacementConfig {
  markAllPages: boolean;
  base: DraftSignaturePlacement;
  overrides: Record<string, DraftSignaturePlacement>;
  skippedPages?: number[];
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const formatLetterDate = (value?: string | null) => {
  if (!value || typeof value !== 'string') return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  const dd = String(parsed.getDate()).padStart(2, '0');
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const hasMeaningfulContent = (html: string) => {
  if (/<(img|svg|canvas|iframe|object|embed|table)\b/i.test(html)) {
    return true;
  }

  const textOnly = html
    .replace(/<br\s*\/?>(\s*)/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<[^>]*>/g, '')
    .trim();
  return textOnly.length > 0;
};

const normalizePreviewHtml = (html: string) => {
  if (!html) return html;

  let cleanedHtml = html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    Array.from(doc.body.querySelectorAll('*')).forEach((el) => {
      // Remove class-based external formatting from pasted sources,
      // but preserve internal sepl-* classes used by preview logic.
      const className = el.getAttribute('class') || '';
      const preservedClasses = className
        .split(/\s+/)
        .map((token) => token.trim())
        .filter((token) => token.startsWith('sepl-'));
      if (preservedClasses.length > 0) {
        el.setAttribute('class', preservedClasses.join(' '));
      } else {
        el.removeAttribute('class');
      }
      el.removeAttribute('id');

      // Re-derive the indentation margin from the persisted `data-indent`
      // level. The editor stores indentation as both `data-indent="N"` and
      // `margin-left: (N * 0.25)in`, but the inline margin-left can be missing
      // in already-saved letters (an older sanitizer stripped inch units),
      // while `data-indent` survives. Without this, indented paragraphs render
      // flush-left in the static PDF. Only applied when no margin-left is set.
      const indentLevel = Number(el.getAttribute('data-indent') || 0);
      if (Number.isFinite(indentLevel) && indentLevel > 0) {
        const existingStyle = el.getAttribute('style') || '';
        if (!/margin-left\s*:/i.test(existingStyle)) {
          const marginLeftRule = `margin-left: ${indentLevel * 0.25}in;`;
          el.setAttribute(
            'style',
            existingStyle ? `${marginLeftRule} ${existingStyle}` : marginLeftRule,
          );
        }
      }

      const style = el.getAttribute('style') || '';

      // List markers are CSS counters, which ignore `<ol start>`. Letters
      // saved before the editor emitted `counter-reset` would otherwise show
      // a list continuing at "3." as "1." here while the editor shows "3.".
      if (el.tagName === 'OL' && !/counter-reset\s*:/i.test(style)) {
        const start = parseInt(el.getAttribute('start') || '', 10);
        if (Number.isFinite(start) && start !== 1) {
          el.setAttribute('style', `counter-reset: li-counter ${start - 1};${style ? ` ${style}` : ''}`);
        }
      }

      if (!el.getAttribute('style')) return;

      const cleanedStyle = (el.getAttribute('style') || '').replace(/;\s*;/g, ';').trim();

      if (cleanedStyle) {
        el.setAttribute('style', cleanedStyle.endsWith(';') ? cleanedStyle : `${cleanedStyle};`);
      } else {
        el.removeAttribute('style');
      }

      const tagName = el.tagName.toLowerCase();
      if (tagName === 'table') {
        const currentStyle = el.getAttribute('style') || '';
        el.setAttribute(
          'style',
          `${currentStyle} border-collapse: collapse !important; border-spacing: 0 !important; margin: 0 !important;`,
        );
      }
      // Cell padding/borders are provided by the scoped `.sepl-letter-prose`
      // table rules (mirroring the editor). Do NOT force `padding: 0` here —
      // that previously cramped table text against the cell borders.
    });

    cleanedHtml = doc.body.innerHTML;
  } catch {
    cleanedHtml = html;
  }

  return cleanedHtml;
};

const sanitizePlacement = (
  input?: Partial<DraftSignaturePlacement> | null,
): DraftSignaturePlacement => sanitizeDraftSignaturePlacement(input, DEFAULT_SIGNATURE_PLACEMENT);

const parseSignatureMarkerClass = (className: string) => {
  const match = className.match(/sepl-signature-meta-p(\d+)-x(\d+)-y(\d+)-w(\d+)-h(\d+)/i);
  if (!match) return null;
  return sanitizePlacement({
    pageIndex: Number(match[1]) || 0,
    x: Number(match[2]),
    y: Number(match[3]),
    width: Number(match[4]),
    height: Number(match[5]),
  });
};

const decodeSignatureConfigToken = (token: string): SignaturePlacementConfig | null => {
  try {
    const parsed = JSON.parse(decodeURIComponent(token));
    const overrides: Record<string, DraftSignaturePlacement> = {};
    Object.entries(parsed?.overrides || {}).forEach(([key, value]) => {
      overrides[String(Math.max(0, Number(key) || 0))] = sanitizePlacement(
        value as Partial<DraftSignaturePlacement>,
      );
    });
    const markAllPages = Boolean(parsed?.markAllPages);
    const rawSkippedPages = Array.isArray(parsed?.skippedPages)
      ? (parsed.skippedPages as unknown[])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n >= 0)
      : [];
    // Honour skipped pages even when "Mark Sig on All Pages" is on: the editor
    // clears stale skips when the toggle is enabled, so saved skips are intentional.
    const skippedPages = rawSkippedPages;

    return {
      markAllPages,
      base: sanitizePlacement(parsed?.base),
      overrides,
      skippedPages,
    };
  } catch {
    return null;
  }
};

const extractSignaturePlacementAndCleanHtml = (html: string) => {
  if (!html || typeof window === 'undefined') {
    return { cleanHtml: html || '', config: null as SignaturePlacementConfig | null };
  }

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let config: SignaturePlacementConfig | null = null;

    const configMarker = doc.querySelector('[class*="sepl-signature-config-"]');
    if (configMarker instanceof HTMLElement) {
      const match = configMarker.className.match(/sepl-signature-config-([^\s]+)/i);
      if (match?.[1]) {
        config = decodeSignatureConfigToken(match[1]);
      }
      configMarker.remove();
    }

    const legacyMarker = doc.querySelector(`.${SIGNATURE_MARKER_CLASS}`);
    if (!config && legacyMarker?.textContent) {
      const parsed = JSON.parse(legacyMarker.textContent);
      config = {
        markAllPages: false,
        base: sanitizePlacement({
          pageIndex: Number(parsed?.pageIndex) || 0,
          x: Number(parsed?.x),
          y: Number(parsed?.y),
          width: Number(parsed?.width),
          height: Number(parsed?.height),
        }),
        overrides: {},
      };
      legacyMarker.remove();
    }

    const classMarker = doc.querySelector('[class*="sepl-signature-meta-"]');
    if (!config && classMarker instanceof HTMLElement) {
      const placement = parseSignatureMarkerClass(classMarker.className);
      if (placement) {
        config = {
          markAllPages: false,
          base: placement,
          overrides: {},
        };
      }
    }
    if (classMarker) {
      classMarker.remove();
    }

    // Cleanup legacy visible JSON remnants
    Array.from(doc.body.querySelectorAll('div, p, span')).forEach((el) => {
      const text = (el.textContent || '').trim();
      if (
        /^\{[\s\S]*"pageIndex"\s*:\s*\d+[\s\S]*"x"\s*:[\s\S]*"y"\s*:[\s\S]*"width"\s*:[\s\S]*"height"\s*:[\s\S]*\}$/.test(
          text,
        )
      ) {
        el.remove();
      }
    });

    return {
      cleanHtml: doc.body.innerHTML || '<p></p>',
      config,
    };
  } catch {
    return { cleanHtml: html, config: null };
  }
};

// Restore stored column widths and row heights from data attributes
const restoreTableDimensions = (html: string): string => {
  if (typeof window === 'undefined') return html;

  const temp = document.createElement('div');
  temp.innerHTML = html;

  const tables = temp.querySelectorAll('table');
  tables.forEach((table) => {
    const htmlTable = table as HTMLElement;
    if (htmlTable.dataset.tableWidth) {
      const width = htmlTable.dataset.tableWidth;
      htmlTable.style.width = width;
      htmlTable.style.maxWidth = width;
    }
    if (htmlTable.dataset.tableHeight) {
      const height = htmlTable.dataset.tableHeight;
      htmlTable.style.height = height;
      htmlTable.style.minHeight = height;
    }
    if (htmlTable.dataset.tableLeft) {
      const tableLeft = Number.parseFloat(String(htmlTable.dataset.tableLeft || '').trim());
      if (Number.isFinite(tableLeft)) {
        htmlTable.style.marginLeft = `${Math.max(0, Math.round(tableLeft))}px`;
      }
    }

    const colElements = table.querySelectorAll('col');
    colElements.forEach((col) => {
      const htmlCol = col as HTMLElement;
      if (htmlCol.dataset.colWidth) {
        const width = htmlCol.dataset.colWidth;
        htmlCol.style.width = width;
        htmlCol.setAttribute('width', width);
      }
    });

    const rows = table.querySelectorAll('tr');
    rows.forEach((row) => {
      const htmlRow = row as HTMLElement;
      if (htmlRow.dataset.rowHeight) {
        const rowHeight = htmlRow.dataset.rowHeight;
        htmlRow.style.height = rowHeight;
        htmlRow.style.minHeight = rowHeight;
      }

      const cells = row.querySelectorAll('td, th');
      cells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const colWidthAttr = htmlCell.getAttribute('colwidth');
        const colWidthFromAttr = colWidthAttr
          ? String(colWidthAttr)
              .split(',')
              .map((part) => Number.parseFloat(part.trim()))
              .find((value) => Number.isFinite(value) && value > 0)
          : null;
        const resolvedColWidth =
          htmlCell.dataset.colWidth ||
          (colWidthFromAttr ? `${Math.round(colWidthFromAttr)}px` : '');
        if (resolvedColWidth) {
          const width = resolvedColWidth;
          htmlCell.dataset.colWidth = width;
          htmlCell.style.width = width;
          htmlCell.style.minWidth = width;
          htmlCell.style.maxWidth = width;
        }
        if (htmlCell.dataset.rowHeight) {
          const height = htmlCell.dataset.rowHeight;
          htmlRow.style.height = height;
          htmlRow.style.minHeight = height;
        }
      });
    });
  });

  return temp.innerHTML;
};

const buildTableHtmlWithRows = (tableEl: HTMLElement, bodyRows: HTMLTableRowElement[]) => {
  const clone = tableEl.cloneNode(true) as HTMLElement;
  const cloneBodies = Array.from(clone.querySelectorAll('tbody'));
  const allCloneRows = Array.from(clone.querySelectorAll('tbody tr, tr'));

  if (cloneBodies.length > 0) {
    cloneBodies.forEach((tbody) => {
      while (tbody.firstChild) tbody.removeChild(tbody.firstChild);
    });
    const firstBody = cloneBodies[0];
    bodyRows.forEach((row) => {
      firstBody.appendChild(row.cloneNode(true));
    });
  } else {
    const theadRows = Array.from(clone.querySelectorAll('thead tr'));
    allCloneRows.forEach((row) => {
      if (!theadRows.includes(row as HTMLTableRowElement)) {
        row.remove();
      }
    });
    bodyRows.forEach((row) => {
      clone.appendChild(row.cloneNode(true));
    });
  }

  return clone.outerHTML;
};

const SignatureImage = ({ src, width, height }: { src: string; width: number; height: number }) => (
  <div
    style={{
      width: `${width}px`,
      height: `${height}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    <img
      src={src}
      alt="Approved signature"
      draggable={false}
      style={{
        objectFit: 'contain',
        maxWidth: '100%',
        maxHeight: '100%',
        width: 'auto',
        height: 'auto',
        display: 'block',
      }}
    />
  </div>
);

const extractFirstImageSrc = (html: string): string | null => {
  if (!html || typeof window === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const img = doc.querySelector('img[src]') as HTMLImageElement | null;
    return img?.src || null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Direct signature renderer — bypasses the HTML blob to avoid the
// "tiny image inside a 722px absolute container" problem.
// Keeps legacy internal offsets (signature.x / signature.y) so final
// signed position stays aligned with existing letter placements.
// ---------------------------------------------------------------------------
const renderApprovedSignatureAtPlacement = (
  approvedHtml: string,
  model: SignatureHtmlModel | null,
  placement: DraftSignaturePlacement,
  showOverlay: boolean,
  applyVisualBoost: boolean,
  blurApprovedSignature: boolean,
  measuredSize?: { width: number; height: number } | null,
) => {
  // Validate placement bounds
  if (placement.width <= 0 || placement.height <= 0 || placement.x < 0 || placement.y < 0) {
    return null;
  }

  const sig = model?.signature;
  const hasImage = !!sig?.src;
  const textHtml = model?.textHtml || '';
  const signatureBlurStyle = blurApprovedSignature
    ? ({ filter: 'blur(18px)', opacity: 0.72 } as const)
    : undefined;

  if (!hasImage && !textHtml) {
    const legacyImageSrc = extractFirstImageSrc(approvedHtml);
    if (legacyImageSrc) {
      return (
        <div
          key="sig-fallback-image"
          className="absolute z-30 pointer-events-none overflow-hidden"
          style={{
            left: `${placement.x}px`,
            top: `${placement.y}px`,
            width: `${placement.width}px`,
            height: `${placement.height}px`,
            ...signatureBlurStyle,
          }}
        >
          <SignatureImage src={legacyImageSrc} width={placement.width} height={placement.height} />
        </div>
      );
    }

    // Fallback: raw HTML blob (legacy / no model) — constrain to placement box
    if (approvedHtml && approvedHtml.trim()) {
      return (
        <div
          key="sig-fallback"
          className="absolute z-30 pointer-events-none overflow-hidden"
          style={{
            left: `${placement.x}px`,
            top: `${placement.y}px`,
            width: `${placement.width}px`,
            height: `${placement.height}px`,
            margin: 0,
            padding: 0,
            ...signatureBlurStyle,
          }}
          dangerouslySetInnerHTML={{ __html: approvedHtml }}
        />
      );
    }
    return null;
  }

  const imgWidth = Math.max(
    40,
    Number(sig?.width) || Number(measuredSize?.width) || placement.width || 180,
  );
  const imgHeight = Math.max(
    24,
    Number(sig?.height) || Number(measuredSize?.height) || placement.height || 90,
  );
  const internalX = sig?.x ? Math.max(0, Number(sig.x)) : 0;
  const internalY = sig?.y ? Math.max(0, Number(sig.y)) : 0;

  // Derive precise placement px values. Prefer stored percentage values
  // (leftPct/topPct/widthPct/heightPct) when available — they preserve
  // sub-pixel precision across save/load and different viewport scales.
  const placedX =
    typeof placement.leftPct === 'number'
      ? (placement.leftPct / 100) * LETTER_PAGE_WIDTH_PX
      : placement.x;
  const placedY =
    typeof placement.topPct === 'number'
      ? (placement.topPct / 100) * LETTER_PAGE_HEIGHT_PX
      : placement.y;
  const placedWidth =
    typeof placement.widthPct === 'number'
      ? (placement.widthPct / 100) * LETTER_PAGE_WIDTH_PX
      : placement.width;
  const placedHeight =
    typeof placement.heightPct === 'number'
      ? (placement.heightPct / 100) * LETTER_PAGE_HEIGHT_PX
      : placement.height;

  // Keep final signed signature within the defined placement box.
  // Allow upscaling so saved signatures can fully occupy the configured box.
  const widthFitScale = placedWidth > 0 ? placedWidth / imgWidth : 1;
  const heightFitScale = placedHeight > 0 ? placedHeight / imgHeight : 1;
  const fitScale = Math.max(0.01, Math.min(widthFitScale, heightFitScale));
  // Export/PDF mode: scale to fit the placement box to avoid stretching/expanding.
  // Preview mode: apply visual boost so the signature appears larger on screen.
  const boostedScale = applyVisualBoost ? fitScale * SIGNATURE_VISUAL_BOOST : fitScale;

  // placement.x / placement.y is the final signature anchor.
  // signature.x / signature.y is the internal image offset inside stored signature html model.
  const scaledInternalX = internalX * boostedScale;
  const scaledInternalY = internalY * boostedScale;
  const wrapperLeft = Math.max(0, placedX - scaledInternalX);
  const wrapperTop = Math.max(0, placedY - scaledInternalY);

  const finalWidth = Math.max(40, imgWidth * boostedScale);
  const finalHeight = Math.max(24, imgHeight * boostedScale);
  const wrapperWidth = Math.max(finalWidth + scaledInternalX, finalWidth);
  const wrapperHeight = Math.max(finalHeight + scaledInternalY, finalHeight);

  return (
    <>
      {showOverlay ? (
        <div
          className="absolute pointer-events-none z-20 border-2 border-dashed border-amber-400 bg-amber-50/10 rounded-sm"
          style={{
            left: `${placement.x}px`,
            top: `${placement.y}px`,
            width: `${finalWidth}px`,
            height: `${finalHeight}px`,
          }}
        >
          <div className="absolute -top-2 left-2 text-[10px] text-amber-700 font-semibold bg-white/90 px-1.5 py-0.5 rounded border border-amber-200 whitespace-nowrap">
            Signature · {formatSignatureBoxLabel(finalWidth, finalHeight)}
          </div>
        </div>
      ) : null}
      {hasImage && sig?.src ? (
        <div
          className="absolute z-30 pointer-events-none overflow-hidden"
          style={{
            left: `${placedX}px`,
            top: `${placedY}px`,
            margin: 0,
            padding: 0,
            width: `${placedWidth}px`,
            height: `${placedHeight}px`,
            ...signatureBlurStyle,
          }}
        >
          {textHtml ? (
            <div
              className="absolute"
              style={{ margin: 0, padding: 0, width: '100%', height: '100%' }}
              dangerouslySetInnerHTML={{ __html: textHtml }}
            />
          ) : null}
          <div
            className="absolute"
            style={{
              left: `${-scaledInternalX}px`,
              top: `${-scaledInternalY}px`,
              margin: 0,
              padding: 0,
            }}
          >
            <SignatureImage
              src={sig.src}
              width={Math.round(finalWidth)}
              height={Math.round(finalHeight)}
            />
          </div>
        </div>
      ) : approvedHtml && approvedHtml.trim() ? (
        <div
          className="absolute z-30 pointer-events-none overflow-hidden"
          style={{
            left: `${placement.x}px`,
            top: `${placement.y}px`,
            margin: 0,
            padding: 0,
            width: `${placement.width}px`,
            height: `${placement.height}px`,
            ...signatureBlurStyle,
          }}
          dangerouslySetInnerHTML={{ __html: approvedHtml }}
        />
      ) : null}
    </>
  );
};

interface LetterDocumentProps {
  subject: string;
  letterDate: string;
  serialNumber?: number | string | null;
  letterNumber?: string | null;
  bodyHtml: string;
  letterheadHtml: string;
  footerHtml: string;
  approvedSignatureHtml?: string | null;
  qrCodeData?: string | null;
  signaturePlacementConfig?: SignaturePlacementConfig | null;
  onDraftSignaturePlacementChange?: (next: {
    pageIndex: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
  onRemoveSignaturePage?: (pageIndex: number) => void;
  enableDraftSignatureDrag?: boolean;
  showPrintBoundaryOverlay?: boolean;
  fitViewport?: boolean;
  exportMode?: boolean;
  blurApprovedSignature?: boolean;
}

export const LetterDocument = forwardRef<HTMLDivElement, LetterDocumentProps>(
  function LetterDocument(
    {
      letterDate,
      letterNumber,
      bodyHtml,
      letterheadHtml,
      footerHtml,
      approvedSignatureHtml,
      qrCodeData,
      signaturePlacementConfig,
      onDraftSignaturePlacementChange,
      onRemoveSignaturePage,
      enableDraftSignatureDrag = false,
      showPrintBoundaryOverlay = false,
      fitViewport = false,
      exportMode = false,
      blurApprovedSignature = false,
    },
    ref,
  ) {
    const viewportContainerRef = useRef<HTMLDivElement | null>(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [headerLoadFailed, setHeaderLoadFailed] = useState(false);
    const [layoutRevision, setLayoutRevision] = useState(0);
    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        viewportContainerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
          return;
        }
        if (ref) {
          (ref as MutableRefObject<HTMLDivElement | null>).current = node;
        }
      },
      [ref],
    );

    const headerSrc = `/letterhead.png?v=${IMAGE_CACHE_BUSTER}`;
    const headerSrcPage2 = `/letterhead2.png?v=${IMAGE_CACHE_BUSTER}`;
    const footerSrc = `/footer.png?v=${IMAGE_CACHE_BUSTER}`;
    const shouldRenderDefaultHeader =
      !headerLoadFailed && (typeof letterheadHtml !== 'string' || letterheadHtml.trim().length > 0);
    const normalizedFooterHtml = useMemo(() => {
      const normalized = (footerHtml || '')
        .replace(/letter-foot\.jpg/g, 'footer.png')
        .replace(/letterfooter\.png(?!\?)/g, `footer.png?v=${IMAGE_CACHE_BUSTER}`)
        .replace(/\/letters\/footer\.png/g, '/footer.png')
        .replace(/\/letters\/letterfooter\.png/g, '/footer.png');

      if (!normalized || typeof window === 'undefined') {
        return normalized;
      }

      try {
        const doc = new DOMParser().parseFromString(normalized, 'text/html');

        Array.from(doc.body.querySelectorAll('*')).forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          const style = el.getAttribute('style') || '';
          const cleanedStyle = style
            .replace(/(^|;)\s*margin\s*:[^;]*/gi, '')
            .replace(/(^|;)\s*padding\s*:[^;]*/gi, '')
            .replace(/(^|;)\s*line-height\s*:[^;]*/gi, '')
            .trim();
          const baseStyle = 'margin: 0; padding: 0; line-height: 0;';
          el.setAttribute('style', cleanedStyle ? `${baseStyle} ${cleanedStyle}` : baseStyle);
        });

        Array.from(doc.body.querySelectorAll('img')).forEach((img) => {
          const src = img.getAttribute('src') || '';
          if (src.includes('letter-foot.jpg')) {
            img.setAttribute(
              'src',
              src.replace(/letter-foot\.jpg/g, `letterfooter.png?v=${IMAGE_CACHE_BUSTER}`),
            );
          } else if (/letterfooter\.png(?!\?)/.test(src)) {
            img.setAttribute(
              'src',
              src.replace(/letterfooter\.png/g, `letterfooter.png?v=${IMAGE_CACHE_BUSTER}`),
            );
          }
          img.setAttribute(
            'style',
            'display: block; width: 100%; height: auto; margin: 0; padding: 0;',
          );
        });

        return doc.body.innerHTML;
      } catch {
        return normalized;
      }
    }, [footerHtml]);
    const parsedBody = useMemo(() => extractSignaturePlacementAndCleanHtml(bodyHtml), [bodyHtml]);

    // Trigger deterministic re-measure passes after HTML/font/image readiness
    // to avoid first-open pagination/signature drift across users and sessions.
    useEffect(() => {
      let cancelled = false;

      const bump = () => {
        if (!cancelled) {
          setLayoutRevision((prev) => prev + 1);
        }
      };

      const t1 = window.setTimeout(bump, 80);
      const t2 = window.setTimeout(bump, 320);

      const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
      if (fonts?.ready) {
        fonts.ready.then(bump).catch(() => undefined);
      }

      return () => {
        cancelled = true;
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      };
    }, [parsedBody.cleanHtml, letterheadHtml, footerHtml, approvedSignatureHtml]);

    const pageChunks = useMemo(() => {
      const getChunkMaxBodyHeight = (pageIndex: number) =>
        pageIndex === 0 ? MAX_BODY_HEIGHT_FIRST_PAGE_PX : MAX_BODY_HEIGHT_SUBSEQUENT_PAGES_PX;
      const restoredBodyHtml = normalizePreviewHtml(restoreTableDimensions(parsedBody.cleanHtml));
      const fallback = [restoredBodyHtml || '<p></p>'];
      if (typeof window === 'undefined' || !restoredBodyHtml) return fallback;

      try {
        const container = document.createElement('div');
        container.innerHTML = restoredBodyHtml;

        // For uploaded PDFs, split pages using explicit wrappers.
        // This avoids image-load timing issues in scrollHeight measurement.
        const pdfPageNodes = Array.from(container.querySelectorAll<HTMLElement>('.sepl-pdf-page'));
        if (pdfPageNodes.length > 0) {
          const pdfChunks = pdfPageNodes
            .map((node) => node.outerHTML)
            .filter((chunk) => hasMeaningfulContent(chunk));
          if (pdfChunks.length > 0) {
            return pdfChunks;
          }
        }

        const unwrapRenderableNodes = (inputNodes: Node[]): Node[] => {
          return inputNodes.flatMap((node) => {
            if (!(node instanceof HTMLElement)) {
              return [node];
            }

            const tag = node.tagName.toLowerCase();
            const isWrapperTag = tag === 'div' || tag === 'section' || tag === 'article';
            const hasOnlyWhitespaceText = Array.from(node.childNodes).every((child) => {
              if (child.nodeType === Node.TEXT_NODE) {
                return !(child.textContent || '').trim();
              }
              return true;
            });
            const hasElementChildren = Array.from(node.childNodes).some(
              (child) => child.nodeType === Node.ELEMENT_NODE,
            );

            if (isWrapperTag && hasElementChildren && hasOnlyWhitespaceText) {
              return unwrapRenderableNodes(Array.from(node.childNodes));
            }

            return [node];
          });
        };

        const nodes = unwrapRenderableNodes(Array.from(container.childNodes));
        if (!nodes.length) return fallback;

        const chunks: string[] = [];
        let current = '';

        const measureHost = document.createElement('div');
        measureHost.style.position = 'fixed';
        measureHost.style.left = '-99999px';
        measureHost.style.top = '0';
        measureHost.style.width = CONTENT_BAND_WIDTH;
        measureHost.style.visibility = 'hidden';
        measureHost.style.pointerEvents = 'none';
        document.body.appendChild(measureHost);

        const measureBody = document.createElement('div');
        measureBody.className = `max-w-none sepl-letter-prose ${BODY_RENDER_CLASSES}`;
        measureBody.style.fontSize = '11pt';
        measureBody.style.fontFamily = LETTER_BODY_FONT_FAMILY;
        measureBody.style.lineHeight = String(LETTER_BODY_LINE_HEIGHT);
        measureHost.appendChild(measureBody);

        nodes.forEach((node) => {
          const rawText = node.textContent || '';
          const trimmedText = rawText.trim();

          let nodeHtml =
            node.nodeType === Node.TEXT_NODE
              ? `<div style="white-space: pre-wrap;">${escapeHtml(rawText)}</div>`
              : (node as HTMLElement).outerHTML || '';

          if (!nodeHtml.trim()) return;
          if (node.nodeType === Node.TEXT_NODE && !trimmedText) return;

          const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : null;
          const tagName = element?.tagName?.toLowerCase() || '';

          // Treat explicit editor page-break markers as hard page boundaries.
          // This keeps submitted/approved pagination aligned with editor preview
          // and avoids trailing blank pages when the last node is a page break.
          const isExplicitPageBreak = Boolean(
            element &&
            (element.classList.contains('page-break') ||
              element.getAttribute('data-page-break') === 'true'),
          );
          if (isExplicitPageBreak) {
            if (current.trim()) {
              chunks.push(current);
            }
            current = '';
            return;
          }

          // ── Adjust large margin-top for pagination (drag positioning) ──
          // In the editor, margin-top represents an absolute document position.
          // When paginating, convert it to a page-relative position and insert
          // blank filler pages for any full pages the margin spans.
          if (element && element.style.marginTop) {
            const rawMt = parseFloat(element.style.marginTop || '0');
            if (rawMt > 0) {
              measureBody.innerHTML = current || '';
              const currentH = current.trim() ? measureBody.scrollHeight : 0;
              const remainingOnPage = getChunkMaxBodyHeight(chunks.length) - currentH;
              const excessMargin = rawMt - remainingOnPage;

              if (excessMargin > 0) {
                // Push current content (or a blank filler) as a completed page
                if (current.trim()) {
                  chunks.push(current);
                } else {
                  chunks.push('<p>&nbsp;</p>');
                }
                current = '';

                // Skip any additional full pages consumed entirely by the margin
                let adj = excessMargin;
                while (adj > getChunkMaxBodyHeight(chunks.length)) {
                  adj -= getChunkMaxBodyHeight(chunks.length);
                  chunks.push('<p>&nbsp;</p>');
                }

                element.style.marginTop = `${Math.round(Math.max(0, adj))}px`;
                nodeHtml = element.outerHTML;
              }
            }
          }

          if (tagName === 'table' && element) {
            const tableRows = Array.from(element.querySelectorAll<HTMLTableRowElement>('tbody tr'));
            const bodyRows =
              tableRows.length > 0
                ? tableRows
                : Array.from(element.querySelectorAll<HTMLTableRowElement>('tr')).filter(
                    (tr) => !tr.closest('thead'),
                  );

            if (bodyRows.length > 1) {
              // Atomic-table rule: only split rows across pages if the
              // table genuinely cannot fit on a single fresh page. For
              // tables that fit on a clean page, fall through to the
              // generic per-node logic, which will push the current
              // chunk and start a new page with the whole table intact.
              const fullTableHtml = buildTableHtmlWithRows(element, bodyRows);
              const freshPageMaxBodyHeight = getChunkMaxBodyHeight(chunks.length + 1);
              measureBody.innerHTML = fullTableHtml;
              const wholeTableFitsFreshPage = measureBody.scrollHeight <= freshPageMaxBodyHeight;

              if (!wholeTableFitsFreshPage) {
                let remainingRows = [...bodyRows];
                let pagePrefix = current;
                let paginatedTable = false;

                while (remainingRows.length > 0) {
                  let fitCount = 0;

                  for (let i = 1; i <= remainingRows.length; i += 1) {
                    const partialTableHtml = buildTableHtmlWithRows(
                      element,
                      remainingRows.slice(0, i),
                    );
                    measureBody.innerHTML = `${pagePrefix}${partialTableHtml}`;
                    if (measureBody.scrollHeight <= getChunkMaxBodyHeight(chunks.length)) {
                      fitCount = i;
                    } else {
                      break;
                    }
                  }

                  if (fitCount === 0) {
                    break;
                  }

                  if (fitCount === remainingRows.length) {
                    if (paginatedTable) {
                      current = buildTableHtmlWithRows(element, remainingRows);
                      pagePrefix = '';
                      remainingRows = [];
                      break;
                    }

                    const wholeHtml = buildTableHtmlWithRows(element, remainingRows);
                    current = `${pagePrefix}${wholeHtml}`;
                    paginatedTable = true;
                    remainingRows = [];
                    break;
                  }

                  const fittedPart = buildTableHtmlWithRows(
                    element,
                    remainingRows.slice(0, fitCount),
                  );
                  chunks.push(`${pagePrefix}${fittedPart}`);
                  remainingRows = remainingRows.slice(fitCount);
                  pagePrefix = '';
                  paginatedTable = true;
                }

                if (paginatedTable) {
                  if (!remainingRows.length && !current) {
                    current = '';
                  }
                  return;
                }
              }
            }
          }

          measureBody.innerHTML = current || '<p></p>';

          const next = `${current}${nodeHtml}`;
          measureBody.innerHTML = next || '<p></p>';
          const nextHeight = measureBody.scrollHeight;

          const currentPageMaxBodyHeight = getChunkMaxBodyHeight(chunks.length);
          // Comfort margin: a table whose bottom would land within this
          // many pixels of the page-content end is treated as overflowing,
          // so the whole table gets pushed to the next page instead of
          // hugging the footer area. Paragraphs and other inline blocks
          // use a tighter check so we don't push every paragraph that
          // ends near the boundary.
          const nodeComfortPx = tagName === 'table' ? 24 : 0;
          const overflow = nextHeight - (currentPageMaxBodyHeight - nodeComfortPx);

          // Per-node "fits on a fresh page" check: if appending this node
          // would overflow the current page AND the node alone fits on a
          // fresh page, push the current chunk and start the new page
          // with this node intact. This keeps tables and long paragraphs
          // from being split across pages whenever they can live on a
          // clean page by themselves.
          let nodeFitsOnFreshPage = false;
          if (overflow > 0) {
            const freshPageMaxBodyHeight = getChunkMaxBodyHeight(chunks.length + 1);
            measureBody.innerHTML = nodeHtml;
            nodeFitsOnFreshPage = measureBody.scrollHeight <= freshPageMaxBodyHeight;
            measureBody.innerHTML = next || '<p></p>';
          }

          if (current && overflow > 0 && nodeFitsOnFreshPage) {
            chunks.push(current);
            current = nodeHtml;
          } else {
            current = next;
          }
        });

        if (current.trim()) chunks.push(current);

        const filteredChunks = chunks.filter((chunk) => hasMeaningfulContent(chunk));

        if (document.body.contains(measureHost)) document.body.removeChild(measureHost);
        if (filteredChunks.length) return filteredChunks;
        return hasMeaningfulContent(fallback[0]) ? fallback : ['<p></p>'];
      } catch (error) {
        console.warn('Error chunking pages:', error);
        return fallback;
      }
    }, [parsedBody.cleanHtml, layoutRevision]);

    const displayDate = formatLetterDate(letterDate);
    const pageScale = fitViewport && !exportMode ? previewScale : 1;

    useEffect(() => {
      if (!fitViewport || exportMode) {
        setPreviewScale(1);
        return;
      }

      const container = viewportContainerRef.current;
      if (!container) return;

      const updateScale = () => {
        const widthPadding = 0;
        const availableWidth = Math.max(0, container.clientWidth - widthPadding);

        if (!availableWidth) {
          setPreviewScale(1);
          return;
        }

        const widthScale = availableWidth / LETTER_PAGE_WIDTH_PX;
        const nextScale = Math.min(1, widthScale);
        setPreviewScale(Math.max(0.55, nextScale));
      };

      updateScale();

      const observer =
        typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScale) : null;
      observer?.observe(container);
      window.addEventListener('resize', updateScale);

      return () => {
        observer?.disconnect();
        window.removeEventListener('resize', updateScale);
      };
    }, [fitViewport, exportMode]);

    const [dragState, setDragState] = useState<{
      pageIndex: number;
      dx: number;
      dy: number;
    } | null>(null);
    const normalizedApprovedSignatureHtml = useMemo(
      () => normalizeSignatureHtmlForRender(approvedSignatureHtml),
      [approvedSignatureHtml],
    );
    const approvedSignatureModel = useMemo(
      () => parseSignatureModelFromHtml(normalizedApprovedSignatureHtml || ''),
      [normalizedApprovedSignatureHtml],
    );
    const approvedSignatureMeasuredSize = useMemo(
      () => measureRenderedSignatureHtml(normalizedApprovedSignatureHtml || ''),
      [normalizedApprovedSignatureHtml],
    );
    const effectiveConfig = useMemo(() => {
      const incoming = signaturePlacementConfig || parsedBody.config;
      if (!incoming) return null;
      const maxIndex = Math.max(0, pageChunks.length - 1);
      const overrides: Record<string, DraftSignaturePlacement> = {};
      Object.entries(incoming.overrides || {}).forEach(([key, value]) => {
        overrides[String(Math.max(0, Number(key) || 0))] = sanitizePlacement(value);
      });
      return {
        markAllPages: Boolean(incoming.markAllPages),
        base: {
          ...sanitizePlacement(incoming.base),
          pageIndex: incoming.markAllPages
            ? Math.min(Math.max(0, Number(incoming.base?.pageIndex) || 0), maxIndex)
            : maxIndex,
        },
        overrides,
        skippedPages: Array.isArray(incoming.skippedPages)
          ? (incoming.skippedPages as number[]).filter((n) => typeof n === 'number')
          : [],
      } as SignaturePlacementConfig;
    }, [pageChunks.length, parsedBody.config, signaturePlacementConfig]);

    const getPlacementForPage = useCallback(
      (pageIndex: number): DraftSignaturePlacement | null => {
        if (!effectiveConfig) return null;

        // Skip pages the user explicitly removed when markAllPages is on
        if (effectiveConfig.markAllPages && effectiveConfig.skippedPages?.includes(pageIndex))
          return null;

        if (!effectiveConfig.markAllPages) {
          return effectiveConfig.base.pageIndex === pageIndex ? effectiveConfig.base : null;
        }

        const override = effectiveConfig.overrides?.[String(pageIndex)];
        if (override) {
          return { ...sanitizePlacement(override), pageIndex };
        }

        return {
          ...sanitizePlacement(effectiveConfig.base),
          pageIndex,
        };
      },
      [effectiveConfig],
    );

    useEffect(() => {
      if (!dragState || !enableDraftSignatureDrag || !onDraftSignaturePlacementChange) return;

      const onMove = (event: MouseEvent) => {
        const page = document.getElementById(`sepl-letter-page-${dragState.pageIndex}`);
        if (!page) return;
        const rect = page.getBoundingClientRect();
        const activePlacement =
          getPlacementForPage(dragState.pageIndex) ||
          sanitizePlacement(DEFAULT_SIGNATURE_PLACEMENT);
        const width = Math.max(80, activePlacement.width || 168);
        const height = Math.max(64, activePlacement.height || 140);
        const pageScaleValue = Number(page.getAttribute('data-preview-scale') || '1') || 1;
        const scaledWidth = width * pageScaleValue;
        const scaledHeight = height * pageScaleValue;
        const x =
          Math.max(
            0,
            Math.min(rect.width - scaledWidth, event.clientX - rect.left - dragState.dx),
          ) / pageScaleValue;
        const y =
          Math.max(
            0,
            Math.min(rect.height - scaledHeight, event.clientY - rect.top - dragState.dy),
          ) / pageScaleValue;
        onDraftSignaturePlacementChange({
          pageIndex: dragState.pageIndex,
          x,
          y,
          width,
          height,
        });
      };

      const onUp = () => setDragState(null);

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
    }, [dragState, enableDraftSignatureDrag, getPlacementForPage, onDraftSignaturePlacementChange]);

    return (
      <div
        ref={setContainerRef}
        className={`${fitViewport ? 'w-[794px] max-w-full mx-auto h-auto overflow-visible' : ''} ${exportMode ? 'sepl-pdf-export-root' : ''}`.trim()}
        style={
          exportMode
            ? {
                width: '794px',
                minWidth: '794px',
                maxWidth: '794px',
                margin: 0,
                padding: 0,
                overflow: 'visible',
              }
            : undefined
        }
      >
        <style>{`
        .sepl-letter-page .sepl-pdf-page {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 0;
          margin-top: 0;
        }

        .sepl-pdf-export-root,
        .sepl-pdf-export-root * {
          zoom: 1 !important;
        }

        .sepl-pdf-export-root .sepl-pdf-capture-page,
        .sepl-pdf-export-root .sepl-letter-page {
          transform: none !important;
        }
      `}</style>
        <div
          className={exportMode ? 'sepl-pdf-export-pages' : 'flex flex-col items-center space-y-6'}
          style={
            exportMode ? { width: '794px', margin: 0, padding: 0, display: 'block' } : undefined
          }
        >
          {pageChunks.map((pageHtml, index) => {
            const isPdfBodyPage = /sepl-pdf-page/.test(pageHtml);
            return (
              <div
                key={`page-${index + 1}`}
                className={`relative ${exportMode ? 'sepl-pdf-capture-page' : ''}`.trim()}
                style={{
                  width: `${Math.round(LETTER_PAGE_WIDTH_PX * pageScale)}px`,
                  height: `${Math.round(LETTER_PAGE_HEIGHT_PX * pageScale)}px`,
                  minHeight: `${Math.round(LETTER_PAGE_HEIGHT_PX * pageScale)}px`,
                  flexShrink: 0,
                  overflow: exportMode ? 'hidden' : undefined,
                }}
              >
                <div
                  id={`sepl-letter-page-${index}`}
                  data-preview-scale={pageScale}
                  className={`sepl-letter-page ${exportMode ? 'relative' : 'absolute left-0 top-0'} bg-white text-gray-900 overflow-hidden flex flex-col pt-0`}
                  style={{
                    width: `${LETTER_PAGE_WIDTH_PX}px`,
                    height: `${LETTER_PAGE_HEIGHT_PX}px`,
                    pageBreakAfter: index < pageChunks.length - 1 ? ('always' as const) : 'auto',
                    breakInside: 'avoid',
                    transform: pageScale !== 1 ? `scale(${pageScale})` : undefined,
                    transformOrigin: 'top left',
                    boxShadow: exportMode ? 'none' : '0 6px 24px rgba(0,0,0,0.35)',
                  }}
                >
                  {showPrintBoundaryOverlay && !exportMode ? (
                    <>
                      <div
                        className="absolute pointer-events-none z-20 border-2 border-dashed border-emerald-400 bg-emerald-50/10 rounded-sm"
                        style={{
                          left: '0px',
                          top: '0px',
                          width: `${LETTER_PAGE_WIDTH_PX}px`,
                          height: `${LETTER_PAGE_HEIGHT_PX}px`,
                        }}
                      >
                        <div className="absolute top-2 right-2 text-[10px] text-emerald-700 font-semibold bg-white/90 px-1.5 py-0.5 rounded border border-emerald-200">
                          Print Boundary
                        </div>
                      </div>
                      <div
                        className="absolute pointer-events-none z-20 border border-dashed border-violet-400 bg-violet-50/10 rounded-sm"
                        style={{
                          left: CONTENT_PAD_LEFT,
                          top: '116px',
                          width: CONTENT_BAND_WIDTH,
                          height: '850px',
                        }}
                      >
                        <div className="absolute -top-2 left-3 text-[10px] text-violet-700 font-semibold bg-white/90 px-1.5 py-0.5 rounded border border-violet-200">
                          Printable Content Area
                        </div>
                      </div>
                    </>
                  ) : null}

                  <div
                    className="m-0 p-0 leading-none overflow-hidden"
                    style={{
                      marginTop: 0,
                      paddingBottom: 0,
                      width: '100%',
                      marginLeft: 0,
                      marginRight: 0,
                      minHeight: 140,
                    }}
                  >
                    {shouldRenderDefaultHeader && (
                      <img
                        src={index === 0 ? headerSrc : headerSrcPage2}
                        alt="Letter Head"
                        className="block w-full h-auto m-0 p-0 align-top"
                        onError={() => setHeaderLoadFailed(true)}
                        style={{
                          marginTop: 0,
                          display: 'block',
                          width: '100%',
                          minWidth: '100%',
                          maxWidth: '100%',
                          height: 'auto',
                          margin: 0,
                          padding: 0,
                        }}
                      />
                    )}
                  </div>

                  {index === 0 && (
                    <div
                      className="pt-[0px] pb-3 flex justify-between text-[0.875rem] leading-9 text-gray-600"
                      style={{ paddingLeft: CONTENT_PAD_LEFT, paddingRight: CONTENT_PAD_RIGHT }}
                    >
                      <div className="text-left space-y-1">
                        <div>
                          <span className="font-bold text-gray-800 italic">
                            SEPL Reference Number:
                          </span>{' '}
                          <span className="font-normal text-gray-800 italic">
                            {formatSeplReferenceNumber(letterNumber) || 'Auto-generated'}
                          </span>
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <div>
                          <span className="font-bold text-gray-800 italic">Date:</span>{' '}
                          <span className="font-normal text-gray-800 italic">{displayDate}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    style={
                      isPdfBodyPage
                        ? { padding: 0 }
                        : {
                            paddingLeft: CONTENT_PAD_LEFT,
                            paddingRight: CONTENT_PAD_RIGHT,
                            paddingBottom: '16px',
                            paddingTop: '4px',
                          }
                    }
                  >
                    <div
                      className=""
                      style={{
                        width: isPdfBodyPage ? '100%' : CONTENT_BAND_WIDTH,
                        height: `${isPdfBodyPage ? (index === 0 ? MAX_PDF_BODY_HEIGHT_FIRST_PAGE_PX : MAX_PDF_BODY_HEIGHT_PX) : index === 0 ? MAX_BODY_HEIGHT_FIRST_PAGE_PX : MAX_BODY_HEIGHT_SUBSEQUENT_PAGES_PX}px`,
                        minHeight: `${isPdfBodyPage ? (index === 0 ? MAX_PDF_BODY_HEIGHT_FIRST_PAGE_PX : MAX_PDF_BODY_HEIGHT_PX) : index === 0 ? MAX_BODY_HEIGHT_FIRST_PAGE_PX : MAX_BODY_HEIGHT_SUBSEQUENT_PAGES_PX}px`,
                        maxHeight: `${isPdfBodyPage ? (index === 0 ? MAX_PDF_BODY_HEIGHT_FIRST_PAGE_PX : MAX_PDF_BODY_HEIGHT_PX) : index === 0 ? MAX_BODY_HEIGHT_FIRST_PAGE_PX : MAX_BODY_HEIGHT_SUBSEQUENT_PAGES_PX}px`,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        className={`max-w-none sepl-letter-prose ${BODY_RENDER_CLASSES}`}
                        style={{
                          fontSize: '11pt',
                          fontFamily: LETTER_BODY_FONT_FAMILY,
                          lineHeight: LETTER_BODY_LINE_HEIGHT,
                        }}
                        dangerouslySetInnerHTML={{ __html: pageHtml || '<p></p>' }}
                      />
                    </div>
                  </div>

                  {/* Approved signature — rendered directly from model for pixel-perfect size match */}
                  {approvedSignatureHtml && getPlacementForPage(index)
                    ? renderApprovedSignatureAtPlacement(
                        normalizedApprovedSignatureHtml,
                        approvedSignatureModel,
                        getPlacementForPage(index)!,
                        showPrintBoundaryOverlay && !exportMode,
                        !exportMode,
                        blurApprovedSignature,
                        approvedSignatureMeasuredSize,
                      )
                    : null}

                  {/* Fallback for old letters without placement metadata */}
                  {index === pageChunks.length - 1 && approvedSignatureHtml && !effectiveConfig ? (
                    <div className="px-14 mt-2 pb-3">
                      <div className="flex justify-start">
                        <div
                          className="w-auto text-left overflow-visible"
                          style={
                            blurApprovedSignature
                              ? { filter: 'blur(18px)', opacity: 0.72 }
                              : undefined
                          }
                          dangerouslySetInnerHTML={{ __html: normalizedApprovedSignatureHtml }}
                        />
                      </div>
                    </div>
                  ) : null}

                  {/* Draft signature placeholder (draggable) */}
                  {!approvedSignatureHtml && getPlacementForPage(index) ? (
                    <div
                      className={`${enableDraftSignatureDrag ? 'cursor-move' : ''} border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50/95 text-xs text-gray-500 font-medium absolute z-30 select-none`}
                      style={{
                        left: `${getPlacementForPage(index)?.x || 0}px`,
                        top: `${getPlacementForPage(index)?.y || 0}px`,
                        width: `${getPlacementForPage(index)?.width || 168}px`,
                        height: `${getPlacementForPage(index)?.height || 140}px`,
                      }}
                      onMouseDown={(event) => {
                        if (!enableDraftSignatureDrag || !onDraftSignaturePlacementChange) return;
                        const currentTarget = event.currentTarget as HTMLElement;
                        const rect = currentTarget.getBoundingClientRect();
                        setDragState({
                          pageIndex: index,
                          dx: event.clientX - rect.left,
                          dy: event.clientY - rect.top,
                        });
                      }}
                    >
                      Signature Box
                      {enableDraftSignatureDrag &&
                      effectiveConfig?.markAllPages &&
                      onRemoveSignaturePage ? (
                        <button
                          type="button"
                          title="Remove signature from this page"
                          className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-800 border border-red-300 cursor-pointer z-40"
                          style={{ fontSize: '11px', lineHeight: 1 }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveSignaturePage(index);
                          }}
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="mt-auto shrink-0 leading-none relative overflow-visible m-0 p-0">
                    {normalizedFooterHtml ? (
                      <div
                        className="m-0 p-0 leading-none overflow-hidden [&_*]:m-0 [&_*]:p-0 [&_img]:block [&_img]:w-full [&_img]:h-auto [&_img]:m-0 [&_img]:p-0"
                        style={{ transform: `translateY(-${FOOTER_VISUAL_SHIFT_PX}px)` }}
                        dangerouslySetInnerHTML={{ __html: normalizedFooterHtml }}
                      />
                    ) : (
                      <img
                        src={footerSrc}
                        alt="Letter Footer"
                        className="block w-full h-auto m-0 p-0"
                        style={{ transform: `translateY(-${FOOTER_VISUAL_SHIFT_PX}px)` }}
                      />
                    )}
                    <div
                      className="absolute text-[10px] text-gray-700 font-medium z-10"
                      style={{
                        bottom: `${QR_INSET_PX - 14}px`,
                        right: `${QR_INSET_PX + QR_BOX_SIZE_PX + 10}px`,
                      }}
                    >
                      Page {index + 1} of {pageChunks.length}
                    </div>
                  </div>

                  {/* QR Code: fixed size, fully visible, and consistently anchored on each page */}
                  {qrCodeData ? (
                    <div
                      className="absolute bg-white rounded-md border border-gray-300 shadow-sm z-20 overflow-hidden"
                      style={{
                        width: `${QR_BOX_SIZE_PX}px`,
                        height: `${QR_BOX_SIZE_PX}px`,
                        bottom: `${QR_INSET_PX - 14}px`,
                        right: `${QR_INSET_PX}px`,
                        padding: `${QR_BOX_PADDING_PX}px`,
                      }}
                    >
                      <img
                        src={qrCodeData}
                        alt="QR Code"
                        className="block w-full h-full object-contain"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
